import keyBy from 'lodash/keyBy';
import { useCallback, useState } from 'react';

import useTypedSWR from 'src/components/general/useTypedSWR';
import { Staff_PopInClinicSettingDataType as PopInClinicSettingDataType } from 'src/generated/gql/graphql';
import { POP_IN_CLINIC_SETTINGS_QUERY } from 'src/popInClinic/settings/popInClinicSettings.gql';
import { SAVE_POP_IN_CLINIC_SETTINGS } from 'src/popInClinic/settings/savePopInClinicSettings.gql';
import useApolloClient from 'src/shared/client/useApolloClient';

type PopInClinicSettings = Partial<Record<string, { dataType: string; value: unknown }>>;

export type SettingUpdate = {
  name: string;
  value: boolean | number;
  dataType: PopInClinicSettingDataType;
};

export function usePopInClinicSettings(): {
  clearSaveError: () => void;
  hasError: boolean;
  isLoading: boolean;
  isSaving: boolean;
  saveError: boolean;
  settings: PopInClinicSettings;
  updateSettings: (updates: SettingUpdate[]) => Promise<boolean>;
} {
  const apolloClient = useApolloClient();
  const { data, error, isLoading, mutate } = useTypedSWR(POP_IN_CLINIC_SETTINGS_QUERY);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  const settings: PopInClinicSettings = data ? keyBy(data.staff_popInClinicSettings, 'name') : {};

  const updateSettings = useCallback(
    async (updates: SettingUpdate[]) => {
      setIsSaving(true);
      try {
        try {
          await apolloClient.mutate({
            mutation: SAVE_POP_IN_CLINIC_SETTINGS,
            variables: { settings: updates },
          });
        } catch {
          setSaveError(true);
          return false;
        }
        // Save persisted; revalidation is best-effort. A refetch failure must not flip
        // success, or a retry would re-stamp the already-saved row.
        try {
          await mutate();
        } catch {
          // already saved — ignore
        }
        return true;
      } finally {
        setIsSaving(false);
      }
    },
    [apolloClient, mutate],
  );

  return {
    clearSaveError: () => setSaveError(false),
    hasError: Boolean(error),
    isLoading,
    isSaving,
    saveError,
    settings,
    updateSettings,
  };
}
